/**
 * TURNS ONE BAR'S DIAGNOSTICS INTO SOMETHING READABLE, and nothing else.
 *
 * <p>Pure functions over the `diagnostics` block each timeline frame carries. Kept out of the
 * component for the reason `dynamanFeed.js` is: jsdom cannot render a chart canvas, so a component
 * test would mock the chart away and then be testing the mock. Every interesting property here is
 * about CONTENT — which gate is closest to price, whether a rule missed by a tick or a mile,
 * whether an absent reading is reported as absent rather than as zero.
 */

/** Frame indices the replay should stop on. A Set, so the clock can test membership per tick. */
export const breakpointSet = (timeline) => new Set(
  (timeline && Array.isArray(timeline.breakpoints) ? timeline.breakpoints : [])
    .filter((i) => Number.isInteger(i) && i >= 0)
);

/**
 * The next trade-entry bar strictly after `index`, or null when none remains.
 *
 * <p>STRICTLY after, so pressing the button while already parked on an entry moves to the NEXT
 * one rather than refusing. A button that does nothing where you currently stand reads as broken.
 *
 * <p>Takes the raw list rather than the Set, because order matters here and a Set has none.
 */
export const nextTradeAfter = (timeline, index) => {
  const stops = timeline && Array.isArray(timeline.breakpoints) ? timeline.breakpoints : [];
  const ahead = stops
    .filter((i) => Number.isInteger(i) && i > index)
    .sort((a, b) => a - b);
  return ahead.length ? ahead[0] : null;
};

/**
 * The frame a trade OPEN at `index` closes on, or null when none is open.
 *
 * <p>This is what lets auto-play stop exactly on the exit bar instead of running into the next
 * trade. Matched by bracketing the playhead between an entry and its exit, which is the only
 * robust test available: the frames carry an `isEntryBar` flag and the trades carry an exit
 * frame, and nothing on the wire pairs the two directly.
 *
 * <p>INCLUSIVE at both ends. At the entry bar the trade has just opened and its exit is still
 * ahead; at the exit bar the trade is closing ON this bar, and reporting null there would make
 * auto-play unable to recognise that it had arrived.
 */
export const openTradeExitAt = (timeline, index) => {
  const stops = Array.isArray(timeline?.breakpoints) ? timeline.breakpoints : [];
  const stamped = Array.isArray(timeline?.trades) ? timeline.trades : [];

  // Entries ascending, so the LAST one at or before the playhead is the live premise's.
  const entriesAtOrBefore = stops
    .filter((i) => Number.isInteger(i) && i <= index)
    .sort((a, b) => a - b);
  if (!entriesAtOrBefore.length) return null;
  const entry = entriesAtOrBefore[entriesAtOrBefore.length - 1];

  // The first exit at or after that entry. The engine holds one premise at a time, so the first
  // exit after an entry is that entry's exit — the reason this pairing is sound without an id.
  const exits = stamped
    .map((t) => t && t.frameIndex)
    .filter((i) => Number.isInteger(i) && i >= entry)
    .sort((a, b) => a - b);
  if (!exits.length) return null;

  const exit = exits[0];
  // Past the exit there is no open trade, even though an earlier entry still precedes us.
  return index <= exit ? exit : null;
};

/** The previous trade-entry bar strictly before `index`, or null. */
export const previousTradeBefore = (timeline, index) => {
  const stops = timeline && Array.isArray(timeline.breakpoints) ? timeline.breakpoints : [];
  const behind = stops
    .filter((i) => Number.isInteger(i) && i < index)
    .sort((a, b) => b - a);
  return behind.length ? behind[0] : null;
};

/**
 * The gate readings, ordered by how close each one is to price.
 *
 * <p>CLOSEST FIRST, because "which gate is about to end this trade" is the question being asked
 * and a fixed order makes a reader compute it themselves every bar. A gate with no opinion sorts
 * LAST regardless — it cannot be close to anything, and putting nulls first would bury the live
 * readings under them.
 *
 * <p>The distance is signed on the wire: positive means the level still sits adversely of price,
 * negative means price has already traded through it. Sorting on the raw value therefore puts a
 * breached gate above a merely near one, which is correct — it is the most urgent thing on screen.
 */
export const gatesByProximity = (gates) => {
  if (!Array.isArray(gates)) return [];
  return [...gates].sort((a, b) => {
    const an = a.distance === null || a.distance === undefined;
    const bn = b.distance === null || b.distance === undefined;
    if (an && bn) return 0;
    if (an) return 1;
    if (bn) return -1;
    return a.distance - b.distance;
  });
};

/** The gate the ratchet actually took this bar, or null when none did. */
export const bindingGate = (gates) =>
  (Array.isArray(gates) ? gates.find((g) => g.binding) : null) || null;

/**
 * How a gate level reads, or an em dash when it has none.
 *
 * <p>An em dash rather than `0.00`: a gate that has not armed has no level, and a zero in a price
 * column is a value a reader — or a sort — will eventually treat as a price.
 */
export const formatLevel = (value, places = 2) =>
  (value === null || value === undefined || !Number.isFinite(value) ? '—' : value.toFixed(places));

/** A signed distance, with its sign always shown so the side of the line is unambiguous. */
export const formatDistance = (value, places = 2) => {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return `${value >= 0 ? '+' : ''}${value.toFixed(places)}`;
};

/**
 * A rule's shortfall as a percentage, signed, or null when there is nothing to compare.
 *
 * <p>Only worth showing on a FAILURE. "Cleared it by 40%" is noise; "missed it by 0.96%" is the
 * finding, and it is the difference between a rule that is nearly right and one that is nowhere
 * near — which a pass/fail column cannot express.
 */
export const shortfallLabel = (check) => {
  if (!check || check.passed) return null;
  if (check.shortfall === null || check.shortfall === undefined) return null;
  if (!Number.isFinite(check.shortfall)) return null;
  return `short by ${(check.shortfall * 100).toFixed(2)}%`;
};

/**
 * One line per entry rule, ready to render.
 *
 * <p>Order is preserved from the backend, which lists them in the order the ENGINE applies them.
 * Sorting failures to the top would read better and would misrepresent the sequence the FSM
 * actually walked, which is the thing being audited.
 */
export const entryRuleRows = (entry) => {
  if (!entry || !Array.isArray(entry.checks)) return [];
  return entry.checks.map((c) => ({
    rule: c.rule,
    label: c.label,
    passed: !!c.passed,
    // A RULE THE RUN DOES NOT APPLY. Absent on an older response, where every rule was live, so
    // `!== false` rather than a truthy test — defaulting a missing flag to "not consulted" would
    // grey out the whole checklist against a backend that had not been redeployed.
    consulted: c.consulted !== false,
    detail: c.detail || '',
    shortfall: shortfallLabel(c),
    isBlocker: entry.blocker === c.rule,
  }));
};

/**
 * One sentence saying what the FSM concluded on this bar.
 *
 * <p>The three cases are genuinely different and a reader needs them distinguished. "Every rule
 * passed" on a bar with a position already open is NOT a contradiction — the engine does not
 * consult entry rules while capital is deployed — and reading it as one is how somebody concludes
 * the gates are broken when they are simply not being asked.
 */
export const entryVerdict = (diagnostics, hasOpenTrade) => {
  if (!diagnostics || !diagnostics.entry) {
    return 'Entry rules not evaluated on this bar (not a signal-series bar).';
  }
  // WHICH PATH. With the structural entry switched off the channel and the volume are never
  // evaluated, so a sentence naming them as the blocker describes a rule the engine did not run.
  // That is what made valid CTS entries read as BLOCKED on bars the engine had taken.
  const rows = entryRuleRows(diagnostics.entry);
  const skipped = rows.filter((r) => !r.consulted);
  const aside = skipped.length
    ? ` (${skipped.length} structural rule${skipped.length === 1 ? '' : 's'} not applied on this `
      + 'run — the signal provider is the only entry path)'
    : '';

  if (hasOpenTrade) {
    return diagnostics.entry.wouldEnter
      ? `Every rule in force passes — but a premise is already open${aside}.`
      : `Blocked by ${diagnostics.entry.blocker} — and a premise is already open anyway${aside}.`;
  }
  return diagnostics.entry.wouldEnter
    ? `Every rule in force passes — a premise would be committed here${aside}.`
    : `Blocked by ${diagnostics.entry.blocker}${aside}.`;
};

/** Stop updates that landed on a given frame, for the step panel. */
export const stopUpdatesAt = (timeline, frameIndex) => {
  const all = timeline && Array.isArray(timeline.stopUpdates) ? timeline.stopUpdates : [];
  return all.filter((u) => u.frameIndex === frameIndex).map((u) => u.event);
};
